import { type CSSProperties } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { cn } from '@/lib/cn'
import { getGiveaway } from '@/api/endpoints'
import { queryKeys } from '@/api/query-keys'
import { useReveal } from '@/hooks/use-reveal'
import type { Giveaway } from '@/models/giveaway'
import { SkeletonLine } from '../ui/Skeleton'
import { EntriesStrip, EntriesStripSkeleton } from './EntriesStrip'
import { ValueBadge, ValueBadgeSkeleton } from './ValueBadge'

interface GiveawayCardProps {
  giveaway: Giveaway
  onSelect?: (giveaway: Giveaway) => void
  /** Position in the grid, used to stagger the reveal. */
  index?: number
}

/**
 * Port of components/giveaway/grid.vue.
 *
 * Two changes to how it presents: the title wraps to two lines instead of
 * being cut to one ellipsised line — the Quasar card set `white-space: nowrap`,
 * so nearly every real giveaway name was truncated — and the art is used twice,
 * once sharply in the thumbnail and once blurred behind the whole panel, so the
 * card carries the colour of the game it belongs to.
 */
export function GiveawayCard({ giveaway, onSelect, index = 0 }: GiveawayCardProps) {
  const queryClient = useQueryClient()
  const { ref, isIn } = useReveal<HTMLButtonElement>()

  const cover = giveaway.display_cover.src
  const owner = giveaway.owner
  const title = giveaway.name ?? ''

  // Warm the cache while the pointer is on the card, so opening it is instant.
  const prefetch = () => {
    if (giveaway.id === null) return
    void queryClient.prefetchQuery({
      queryKey: queryKeys.giveaway(giveaway.id),
      queryFn: () => getGiveaway(giveaway.id as number),
      staleTime: 30_000,
    })
  }

  const style = {
    '--card-art': cover ? `url(${JSON.stringify(cover)})` : 'none',
    '--d': `${Math.min(index, 8) * 60}ms`,
  } as CSSProperties

  return (
    <button
      ref={ref}
      type="button"
      className={cn('giveaway', 'reveal', isIn && 'is-in', giveaway.has_ended && 'has-ended')}
      style={style}
      onClick={() => onSelect?.(giveaway)}
      onMouseEnter={prefetch}
      onFocus={prefetch}
    >
      <div className="giveaway-header">
        <div className="giveaway-avatar-section">
          <img
            src={owner?.display_profile_picture.src ?? '/icons/logo.png'}
            alt=""
            loading="lazy"
          />
        </div>
        <div className="giveaway-title-section">
          {/* `title` keeps the full name reachable when it does overflow two lines. */}
          <div className="giveaway-title" title={title}>
            {title}
          </div>
          <div className="giveaway-subtitle">
            created by
            <span className="giveaway-username">&nbsp;{owner?.display_name}</span>
          </div>
        </div>
        <ValueBadge gameInfo={giveaway.game_info} />
      </div>

      <div className="giveaway-cover">
        {cover ? <img src={cover} alt="" loading="lazy" /> : null}
      </div>

      {giveaway.description && giveaway.description.length > 1 ? (
        <div className="giveaway-description">{giveaway.description}</div>
      ) : null}

      <EntriesStrip giveaway={giveaway} />
    </button>
  )
}

/**
 * Placeholder shown in the grid while results load. Built from the card's own
 * classes, so it is the card's exact size and layout with the data greyed out.
 */
export function GiveawayCardSkeleton() {
  return (
    <div
      className="giveaway is-skeleton"
      style={{ '--card-art': 'none' } as CSSProperties}
      aria-hidden="true"
    >
      <div className="giveaway-header">
        <div className="giveaway-avatar-section">
          <div className="skeleton giveaway-avatar-skeleton" />
        </div>
        <div className="giveaway-title-section">
          <div className="giveaway-title">
            <SkeletonLine width="88%" />
            <SkeletonLine width="52%" />
          </div>
          <div className="giveaway-subtitle">
            <SkeletonLine width="62%" />
          </div>
        </div>
        <ValueBadgeSkeleton />
      </div>

      <div className="giveaway-cover skeleton" />

      <div className="giveaway-description">
        <SkeletonLine />
        <SkeletonLine width="68%" />
      </div>

      <EntriesStripSkeleton />
    </div>
  )
}
